import { isPostalCode as isPostalCodeValid } from '@jjj/components';
import { ICountry } from '@dash/country';
import { countries as countriesList } from 'country-data';
import { COUNTRY_CONTEXT_KEY, LANGUAGE_KEY } from './localStorageKeys.const';
import { logger } from './logger';
import { COUNTRIES } from './countries';
import { getInitialCountryCode } from '../settings/organizationSettings';
import { getCachedCountriesSettings } from '../redux/countriesSettings/helpers';

const defaultCountry = {
  ...COUNTRIES[getInitialCountryCode()],
  id: 'fca3284c-3485-47bf-b8c1-4d471fcff6c1',
};

// Add additional data based on country name
export const convertCountryContext = (country: ICountry) => {
  return {
    ...country,
    ...(country && COUNTRIES[country.name] && { ...COUNTRIES[country.name] }),
    ...(country && COUNTRIES[country.code] && { ...COUNTRIES[country.code] }),
    ...(country && { id: country.id }),
    ...(country && { name: country.name }),
  };
};

export const getCurrentCountryContext = () => {
  const stored = localStorage.getItem(COUNTRY_CONTEXT_KEY);

  return (stored ? JSON.parse(stored) : null) || defaultCountry;
};

export const getCountryByName = (countryName: string): any | undefined => {
  if (!countriesList || !countryName) return undefined;

  return Object.values(countriesList).find((c: any) => c.name && c.name.toLowerCase() === countryName.toLowerCase());
};

interface SelectedCountry {
  id: string;
  name: string;
}

export const getCountryRestrictions = (
  countries: SelectedCountry[] = [],
): {
  country?: string[];
} => {
  if (!countries.length) return {};

  const codes = countries
    .map(c => getCountryByName(c.name)?.alpha2?.toUpperCase?.())
    .filter((code): code is string => Boolean(code));

  return codes.length ? { country: codes } : {};
};

const setLocalStorageCountryContext = (country: ICountry) => {
  localStorage.setItem(COUNTRY_CONTEXT_KEY, JSON.stringify(convertCountryContext(country)));
};

export const getLocalStorageLanguageData = () => {
  const data = localStorage.getItem(LANGUAGE_KEY);

  return data ? JSON.parse(data) : null;
};

export const setLocalStorageLanguageData = (key: string, value: string) => {
  const updatedLanguage = {
    ...getLocalStorageLanguageData(),
    ...{
      [key]: value,
    },
  };

  localStorage.setItem(LANGUAGE_KEY, JSON.stringify(updatedLanguage));
};

const setCurrentCountryContext = (country: ICountry) => {
  if (!getCurrentCountryContext()) {
    setLocalStorageCountryContext(country || defaultCountry);
  }
};

export const getCountryCodeById = (countries: ICountry[], countryId: string) => {
  let country_code = defaultCountry.code;

  if (countries && countries.length > 0) {
    const [country] = countries.filter(item => item.id === countryId);
    country_code = (country && country.name) || country_code;
  }

  return country_code;
};

export const getCountryConfigById = (id: string, availableCountries: ICountry[]) => {
  return COUNTRIES[getCountryCodeById(availableCountries, id)];
};

export function updateCurrentCountryContext(country: ICountry) {
  setCurrentCountryContext(country);

  if (country) {
    setLocalStorageCountryContext(country);
  }
}

export const setDefaultCountryField = () => {
  return { name: 'country', touched: false, error: '', value: getCurrentCountryContext().id };
};

export const getCountryStatesByCountryId = (countries: ICountry[], countryId: string) => {
  const country = countries && countries.find(countryItem => countryItem.id === countryId);

  return country && country.states ? country.states : [];
};

export const isAUS = () => {
  return getCurrentCountryContext().code === COUNTRIES.AUS.code;
};

export const isISR = () => {
  return getCurrentCountryContext().code === COUNTRIES.ISR.code;
};

export const isUSA = () => {
  return getCurrentCountryContext().code === COUNTRIES.USA.code;
};

export const isGBR = () => {
  return getCurrentCountryContext().code === COUNTRIES.GBR.code;
};

export const getMaxSkillsCount = () => {
  return isAUS() ? 4 : 3;
};

export const formatCurrency = (value: number) => {
  const countriesSettings = getCachedCountriesSettings();

  if (countriesSettings) {
    try {
      const { currency_code: currency, default_locale, alpha_2_code } = countriesSettings;
      const locale = default_locale && alpha_2_code ? `${default_locale}-${alpha_2_code}` : 'en-US';
      const currencyObject = new Intl.NumberFormat(locale, { style: 'currency', currency });

      // This is a feature to change '-' position for negative value
      // Default lib behavior: ₪ -30.00
      // Client requested behavior: - ₪ 30.00
      // this custom character in first replace is added to force browser read it properly
      // see more here: https://www.fileformat.info/info/unicode/char/200f/index.htm
      if (currency === COUNTRIES.ISR.currency) {
        return `${currencyObject.format(Math.abs(value || 0)).replace(/\s+/g, '‏')}${value < 0 ? ' -' : ''}`;
      }

      return currencyObject.format(value || 0);
    } catch (error) {
      logger.warn(
        `Error formatting currency with countriesSettings, falling back to default: ${COUNTRIES.USA.currency}`,
      );

      // Fallback to USA currency
      return new Intl.NumberFormat(COUNTRIES.USA.locale, {
        style: 'currency',
        currency: COUNTRIES.USA.currency,
      }).format(value || 0);
    }
  }

  // Fallback to USA currency
  return new Intl.NumberFormat(COUNTRIES.USA.locale, {
    style: 'currency',
    currency: COUNTRIES.USA.currency,
  }).format(value || 0);
};

export const getNationalIdMask = (countryCode: string) => COUNTRIES[countryCode].nationalIdMask;

export const isPostalCode = (value: string, countryCode?: string): boolean => {
  if (!value) {
    return false;
  }

  if (countryCode) {
    return isPostalCodeValid(value, countryCode);
  }

  const countriesSettings = getCachedCountriesSettings();

  if (!countriesSettings?.zip_code_format) {
    return isPostalCodeValid(value, countriesSettings?.alpha_2_code || COUNTRIES.USA.alpha_2_code);
  }

  if (countriesSettings?.zip_code_format) {
    try {
      let pattern = countriesSettings.zip_code_format as string;
      const hasDoubleBackslash = pattern.indexOf('\\\\') !== -1;

      if (hasDoubleBackslash) {
        pattern = pattern.replace(/\\\\/g, '\\');
      }

      const regex = new RegExp(pattern);
      const trimmedValue = value.trim();

      return regex.test(trimmedValue);
    } catch (error) {
      logger.warn(`Error validating postal code with zip_code_format: ${error}`);

      return isPostalCodeValid(value, countriesSettings?.alpha_2_code || COUNTRIES.USA.alpha_2_code);
    }
  }

  return isPostalCodeValid(value, countriesSettings?.alpha_2_code || COUNTRIES.USA.alpha_2_code);
};

export const getCurrencyConfig = () => {
  const countriesSettings = getCachedCountriesSettings();

  if (countriesSettings) {
    const { currency_code: currency, currency_symbol, default_locale, alpha_2_code } = countriesSettings;
    const locale = default_locale && alpha_2_code ? `${default_locale}-${alpha_2_code}` : 'en-US';

    return {
      currency,
      currency_symbol,
      locale,
    };
  }

  // Fallback to USA currency config if countriesSettings is not available
  return {
    currency: COUNTRIES.USA.currency,
    currency_symbol: COUNTRIES.USA.currency_symbol,
    locale: COUNTRIES.USA.locale,
  };
};
